import { signIn, signUp, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { authAxiosClient } from './instance';

interface DecodedPayload {
  'cognito:username'?: unknown;
  preferred_username?: unknown;
  name?: unknown;
  given_name?: unknown;
  family_name?: unknown;
  phone_number?: unknown;
  email?: unknown;
  profile?: unknown;
}

export interface AuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  user: {
    username: string;
    name: string;
    given_name: string;
    family_name: string;
    phone_number: string;
    email: string;
    profile: string;
    preferred_username: string;
  };
}

const str = (v: unknown): string => (v != null ? String(v) : '');

/**
 * Initiates sign in — sends OTP via Amplify CUSTOM_WITHOUT_SRP flow.
 * Session is managed internally by Amplify (CookieStorage).
 */
export const initiateSignIn = async (phoneNumber: string): Promise<{ session: string }> => {
  const formattedPhone = `+91${phoneNumber}`;
  try {
    await signIn({
      username: formattedPhone,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    });
    return { session: '' };
  } catch (error: any) {
    const msg = error?.message?.toLowerCase() ?? '';
    if (
      error?.name === 'UserNotFoundException' ||
      msg.includes('usernotfoundexception') ||
      msg.includes('user does not exist')
    ) {
      throw new Error('USER_NOT_FOUND');
    }
    throw error;
  }
};

/**
 * Signs up a new user then initiates sign in.
 */
export const signUpUser = async (
  phoneNumber: string,
  firstName?: string,
  lastName?: string,
): Promise<{ session: string }> => {
  const phoneWithoutPrefix = phoneNumber.replace(/^\+91/, '');
  const phoneWithPrefix = `+91${phoneWithoutPrefix}`;
  const username = `91${phoneWithoutPrefix}`;

  const userAttributes: Record<string, string> = {
    phone_number: phoneWithPrefix,
    profile: 'PARENT',
    given_name: firstName || 'Guest',
    family_name: lastName || 'User',
  };

  try {
    await signUp({
      username,
      password: `TempPass@${Math.floor(1000 + Math.random() * 9000)}`,
      options: { userAttributes },
    });
    return initiateSignIn(phoneWithoutPrefix);
  } catch (error: any) {
    if (
      error?.name === 'UsernameExistsException' ||
      error?.message?.includes('UsernameExistsException')
    ) {
      return initiateSignIn(phoneWithoutPrefix);
    }
    throw error;
  }
};

/**
 * Verifies OTP via Amplify confirmSignIn, returns auth result with tokens.
 */
export const verifyOtp = async (
  _phoneNumber: string,
  otp: string,
  _session: string,
): Promise<AuthResult> => {
  const result = await confirmSignIn({ challengeResponse: otp });

  if (!result.isSignedIn) {
    throw new Error('Please enter the correct OTP');
  }

  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString() ?? '';
  const idToken = session.tokens?.idToken?.toString() ?? '';
  const payload = (session.tokens?.idToken?.payload ?? {}) as DecodedPayload;

  return {
    accessToken,
    idToken,
    refreshToken: '', // Amplify manages refresh token internally via CookieStorage
    user: {
      username: str(payload['cognito:username']),
      name: `${str(payload.given_name)} ${str(payload.family_name)}`.trim(),
      given_name: str(payload.given_name),
      family_name: str(payload.family_name),
      phone_number: str(payload.phone_number),
      email: str(payload.email),
      profile: str(payload.profile),
      preferred_username: str(payload.preferred_username),
    },
  };
};

/**
 * Resend OTP — re-initiates sign in flow.
 */
export const resendOtp = async (phoneNumber: string): Promise<{ session: string }> => {
  return initiateSignIn(phoneNumber);
};

/**
 * Refresh access token via Amplify (force refresh bypasses cache).
 */
export const refreshAccessToken = async (_refreshToken: string): Promise<string | null> => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.accessToken?.toString() ?? null;
  } catch (error) {
    console.error('[ERROR] Unexpected error refreshing token:', error);
    return null;
  }
};

// Legacy exports for backward compatibility
export const sendOtpAPI = initiateSignIn;
export const verifyOtpAPI = verifyOtp;

export type UserAttributePayload = {
  Name: string;
  Value: string;
};

/**
 * Update user attributes in Cognito.
 * Access token sourced from Amplify session (not localStorage).
 */
export const updateUserById = async (userAttributes: UserAttributePayload[]): Promise<any> => {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();

  if (!accessToken) {
    throw new Error('No access token available');
  }

  const response = await authAxiosClient.post(
    '/',
    { AccessToken: accessToken, UserAttributes: userAttributes },
    {
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.UpdateUserAttributes',
        'Content-Type': 'application/x-amz-json-1.1',
      },
    },
  );

  return response.data;
};
